import _ from 'lodash'

import { BaseAction, selectCampaign, selectCampaigns, State } from './index'
import {
    BlockedCampaign,
    CampaignsResult,
    CloseCampaignRequest,
    RequestExportCampaignPerformancePayload,
    RequestExportCampaignProgressPayload,
    SubmissionsSummaryResult,
    BlockedCampaignCheckResult,
    RequestExportCampaignOverviewPayload,
    UpdateTotalEarningPayload,
    PaidOutsideAppPayload,
    SaveAdditionalPaymentPayload,
    UpdateServiceFeePayload,
    UpdateTaxPayload,
    UpdatePaymentSummaryPayload,
    GetPaymentSummaryPayload,
    CampaignPaymentSummaryData,
    RegisterCampaignRequest,
    UpdateProfileRecommendationSettingRequest,
    RegisterCampaignResponse,
    ApplyPromoCodePayload,
    UnapplyPromoCodePayload,
    TogglePayWithBalancePayload,
    SetToReadyForLaunchPayload,
} from '../services/api/campaigns'
import { SubmissionStatusCode } from './submissions'
import { ContentData } from '../services/cloudinary'
import { fixBackendDateFormat } from '../services/dates'
import { BrandState } from './brands'
import { COUNTRY_ENUM, STEP_TYPE_ENUM, PLATFORM_ENUM } from 'services/api/constants'
import { createSelector } from 'reselect'
import { STATE_KEYS } from './requests'
import { PaidOutsideAppPayloadWithCallback, UpdateTotalEarningPayloadWithCallback } from 'sagas/campaigns'

export type CAMPAIGN_LIST_TYPES = 'upcoming' | 'invitation' | null

export enum CAMPAIGN_STATUS_ENUM {
    DRAFT = 0,
    FOR_REVIEW = 1,
    LIVE = 2,
    REJECTED = 3,
    PAUSED = 4,
    CLOSED = 5,
    FOR_EDIT = 6,
    WAIT_FOR_PAYMENT = 7,
    READY_FOR_LAUNCH = 8,
    ENDED = 9,
    EXPIRED = 10,
    FULFILLED = 11,
    PAID_OUT = 12,
}

export enum CAMPAIGN_SUBMISSION_STATUS_ENUM {
    OPEN = 0,
    FILLING_FAST,
    FULL,
}

export const CAMPAIGN_VIEW = {
    LIVE: 'live',
    CLOSED: 'closed',
    GUIDED_TEMPLATES: 'guided_templates',
} as const

export type CampaignViews = (typeof CAMPAIGN_VIEW)[keyof typeof CAMPAIGN_VIEW]

export const CAMPAIGN_VIEW_VALUES: readonly CampaignViews[] = [
    CAMPAIGN_VIEW.LIVE,
    CAMPAIGN_VIEW.CLOSED,
    CAMPAIGN_VIEW.GUIDED_TEMPLATES,
]

export type SubmissionsSummaryViews = 'active' | 'closed'

export type TaskSummary = {
    title: string
    total_for_review_submission_count: number
    total_pending_submission_count: number
    total_eligible_pending_submissions_count: number
    redirect_step: {
        review_status: string
        uuid: string
    }
    has_fulfilled_slot: boolean | null
    uuid: string
    latest_submission_photo: ContentData
}

export enum REQUIREMENT_TYPE_ENUM {
    AGE = 1,
    GENDER = 2,
    FOLLOWER = 3,
    OCCUPATION = 4,
    LOCATION = 5,
    PROFILE_TYPE = 6,
    OTHERS = 999,
}

type LocationDetailState = {
    description: string
    id: number
    name: string
    uuid: string
}

export type LocationState = {
    city?: LocationDetailState
    province?: LocationDetailState
    state?: LocationDetailState
    district?: LocationDetailState
    region?: LocationDetailState
}

export type FollowerState = {
    platform: number
    min: number | null
    max: number | null
    uuid: string
}

type OccupationState = {
    value: string
}

type ProfileTypeState = {
    value: string
}

export type RequirementState = {
    type: REQUIREMENT_TYPE_ENUM
    properties: {
        value?: string
        min?: number
        max?: number
        followers?: FollowerState[]
        occupations?: OccupationState[]
        locations?: LocationState[]
        profile_types?: ProfileTypeState[]
    }
    uuid?: string
}

export const TEMPLATE_STATUS = {
    DRAFT: 'draft',
    UPDATABLE: 'updatable',
    FINISHED: 'finished',
} as const

type TemplateStatusType = keyof typeof TEMPLATE_STATUS
export type TemplateStatus = (typeof TEMPLATE_STATUS)[TemplateStatusType]

export interface TemplateData {
    uuid: string
    status: TemplateStatus
}

export interface GuidedTemplateSettingsModalState {
    isOpen: boolean
    mode?: 'guided_template' | 'external_onboarding'
    teamUuid?: string
    isDuplicate?: boolean
    templateDataUuid?: string
}

export type CampaignState = {
    uuid: string
    title: string
    objective: number
    categories: number[]
    status: CAMPAIGN_STATUS_ENUM
    currency: string
    country: COUNTRY_ENUM
    is_private: boolean
    brand: BrandState
    description?: string
    images?: string[]
    requirements: RequirementState[]
    payout_deadline_start?: string
    payout_deadline_end?: string
    payout_summary: {
        min_payout: number
        max_payout: number
    }
    platforms: PLATFORM_ENUM[]
    budget: number
    budget_used: number
    slot_required: boolean
    slot_number: number | null
    auto_update_submission_status: boolean
    batching_enable: boolean
    created_at: string
    submission_status: CAMPAIGN_SUBMISSION_STATUS_ENUM
    submission_status_filling_fast_min: number | null
    submission_status_full_min: number | null
    total_batch_size: number | null
    total_content_submissions: number
    total_finished_submission_count: number
    total_submission_in_waiting_list: number | null
    time_end: string | null
    time_window: {
        start: string
        end: string
    }
    overall_step_time_window: {
        start: string
        end: string
    }
    registration_enable: boolean
    registration_max_slots?: number
    profile_recommendation_enabled: boolean
    blocked_campaigns: BlockedCampaign[]
    self_serve: boolean
    template_data?: TemplateData
    in_template_flow: boolean
    has_budget: boolean
    stripe_payment_link_url: string
    stripe_payment_link_id: string
    platform_content_types?: number[]
    filter_options?: {
        business_challenges?: string[]
        industry?: string[]
        product_type?: string
        product_access_type?: string
    }
}

export type EditingCampaignState = {
    brand: {
        uuid?: string
    }
    objective?: number
    categories?: number[]
    title?: string
    budget?: number
}

type CampaignEntitiesState = {
    [uuid: string]: CampaignState
}

type BlockCampaignEntitiesState = {
    [uuid: string]: BlockedCampaignCheckResult
}

interface SubmissionProgressSummary {
    [key: string]: number
}

export interface CampaignSummaryStep {
    uuid: string
    title: string
    type: STEP_TYPE_ENUM
    time_window: {
        start: string
        end: string
    }
    submission_progress_summary: SubmissionProgressSummary
}

export interface CampaignSummaryTask {
    uuid: string
    title: string
    ordering_index: number
    submission_progress_summary: {
        pending_count: number
        for_review_count: number
    }
    steps: CampaignSummaryStep[]
    has_pending_submissions_to_review: boolean
}

export interface CampaignSummaryState extends CampaignState {
    tasks: CampaignSummaryTask[]
    submission_summary: {
        finished_count: number
        in_progress_count: number
        deleted_count: number
        rejected_count: number
    }
    registration_result_summary: {
        pending_count: number
        waitlisted_count: number
        awaiting_response_count: number
        user_accepted_count: number
        joined_count: number
        declined_invites_count: number
        deleted_count: number
        rejected_count: number
        used_slots_count: number
        total_count: number
    } | null
}

export interface SubmissionSummaryTask {
    uuid: string
    title: string
    total_pending_submission_count: number
    total_for_review_submission_count: number
    total_eligible_pending_submissions_count: number
    redirect_step: {
        review_status: string
        uuid: string
    }
    has_fulfilled_slot: boolean | null
    latest_submission_photo: any
}

export interface SubmissionSummaryState extends CampaignState {
    registration_completed: boolean | null
    tasks: SubmissionSummaryTask[]
    total_finished_submission_count: number
    total_pending_registration_result_count: number | null
    overall_time_window: {
        start: string
        end: string
    }
}

type CampaignSummaryEntitiesState = {
    [uuid: string]: CampaignSummaryState
}

type SubmissionsSummaryEntitiesState = {
    [uuid: string]: SubmissionSummaryState
}

export type CampaignProgressState = {
    uuid: string
    status: SubmissionStatusCode
    submission_tasks: {
        uuid: string
        status: number
        progress: {
            uuid: string
            status: number
            step_uuid: string
        }[]
        task_uuid: string
    }[]
    partiposterId: string
}

export type CampaignPerformanceState = {
    uuid: string
    submission_tasks: {
        uuid: string
        status: number
        performance: any
        task_uuid: string
    }[]
    total_earnings: number
    total_earnings_without_adjustment?: number
    payout_remark?: string
    earnings_adjustment_custom_reason?: string
    earnings_adjustment_reason: string
    earnings_adjustment_source?: string
    status: number
    partiposterId: string
    instagram_follower_count?: number | null
    tiktok_follower_count?: number | null
    twitter_follower_count?: number | null
    youtube_follower_count?: number | null
    facebook_follower_count?: number | null
    xiaohongshu_follower_count?: number | null
    is_paid_outside_app: boolean | null
    paid_outside_app_reason?: string
    paid_outside_app_custom_reason?: string
    paid_outside_app_payment_method?: string
    paid_outside_app_custom_payment_method?: string
}

export interface CampaignSubmissionsSummaryData {
    campaign_uuid: string
    completed_submissions_count: number
    paid_submissions_count: number
    pending_submissions_count: number
    pending_submissions_breakdown: {
        completed_progress_count: number
        required_progress_count: number
        submissions_count: number
    }[]
}

export type CampaignsState = {
    byId: {
        campaigns: CampaignEntitiesState
        campaignSummaries: CampaignSummaryEntitiesState
        submissionsSummaries: SubmissionsSummaryEntitiesState
        checkedBlockedCampaigns: BlockCampaignEntitiesState
    }
    campaigns: {
        [viewType in CampaignViews]: string[]
    }
    submissionsSummaries: {
        [viewType in SubmissionsSummaryViews]: string[]
    }
    campaignPerformance: CampaignPerformanceState[]
    campaignProgress: CampaignProgressState[]
    editingCampaign: string | null
    blockedCampaigns: string[] | null
    paymentSummary?: CampaignPaymentSummaryData
    guidedTemplateSettings: GuidedTemplateSettingsModalState
    campaignSubmissionsSummary: { [uuid: string]: CampaignSubmissionsSummaryData }
}

const initialState: CampaignsState = {
    byId: {
        campaigns: {},
        submissionsSummaries: {},
        campaignSummaries: {},
        checkedBlockedCampaigns: {},
    },
    campaigns: {
        [CAMPAIGN_VIEW.LIVE]: [],
        [CAMPAIGN_VIEW.CLOSED]: [],
        [CAMPAIGN_VIEW.GUIDED_TEMPLATES]: [],
    },
    submissionsSummaries: {
        active: [],
        closed: [],
    },
    campaignProgress: [],
    campaignPerformance: [],
    editingCampaign: null,
    blockedCampaigns: null,
    paymentSummary: undefined,
    guidedTemplateSettings: { isOpen: false },
    campaignSubmissionsSummary: {},
}

export enum CampaignsActions {
    CAMPAIGN_LIST_REQUEST = 'campaigns/CAMPAIGN_LIST_REQUEST',
    SAVE_LIST = 'campaigns/SAVE_LIST',
    DELETE = 'campaigns/DELETE',
    CAMPAIGN_LIST_CLEAR = 'campaigns/CAMPAIGN_LIST_CLEAR',
    CAMPAIGN_REQUEST = 'campaigns/CAMPAIGN_REQUEST',
    SAVE_CAMPAIGN = 'campaigns/SAVE',
    CAMPAIGN_SUMMARY_REQUEST = 'campaigns/CAMPAIGN_SUMMARY_REQUEST',
    SAVE_CAMPAIGN_SUMMARY = 'campaigns/SAVE_SUMMARY',
    SUBMISSIONS_SUMMARY_REQUEST = 'campaigns/SUBMISSIONS_SUMMARY_REQUEST',
    SAVE_SUBMISSIONS_SUMMARY = 'campaigns/SAVE_SUBMISSIONS_SUMMARY',
    SUBMISSIONS_SUMMARY_CLEAR = 'campaigns/SUBMISSIONS_SUMMARY_CLEAR',
    REGISTER_CAMPAIGN_REQUEST = 'campaigns/REGISTER_CAMPAIGN_REQUEST',
    UPDATE_PROFILE_RECOMMENDATION_SETTING_REQUEST = 'campaigns/UPDATE_PROFILE_RECOMMENDATION_SETTING_REQUEST',
    SAVE_PROFILE_RECOMMENDATION_SETTING = 'campaigns/SAVE_PROFILE_RECOMMENDATION_SETTING',
    UPDATE_PAYOUT_DEADLINE_REQUEST = 'campaigns/UPDATE_PAYOUT_DEADLINE_REQUEST',
    SAVE_PAYOUT_DEADLINE = 'campaigns/SAVE_PAYOUT_DEADLINE',
    UPDATE_CAMPAIGN_REQUEST = 'campaigns/UPDATE_CAMPAIGN_REQUEST',
    SUBMIT_CAMPAIGN_REQUEST = 'campaigns/SUBMIT_CAMPAIGN_REQUEST',
    PUBLISH_CAMPAIGN_REQUEST = 'campaigns/PUBLISH_CAMPAIGN_REQUEST',
    SEND_CAMPAIGN_TO_EDIT_REQUEST = 'campaigns/SEND_CAMPAIGN_TO_EDIT_REQUEST',
    SET_CAMPAIGN_SUBMISSION_STATUS = 'campaigns/SET_CAMPAIGN_SUBMISSION_STATUS_REQUEST',
    SAVE_CAMPAIGN_SUBMISSION_STATUS = 'campaigns/SAVE_CAMPAIGN_SUBMISSION_STATUS',
    SET_AUTO_SUBMISSION_STATUS = 'campaigns/SET_AUTO_SUBMISSION_STATUS_REQUEST',
    SAVE_AUTO_SUBMISSION_STATUS = 'campaigns/SAVE_AUTO_SUBMISSION_STATUS',
    CAMPAIGN_PERFORMANCE_REQUEST = 'campaigns/CAMPAIGN_PERFORMANCE_REQUEST',
    CAMPAIGN_PERFORMANCE_FILTERS_REQUEST = 'campaigns/CAMPAIGN_PERFORMANCE_FILTERS_REQUEST',
    SAVE_CAMPAIGN_PERFORMANCE = 'campaigns/SAVE_CAMPAIGN_PERFORMANCE',
    UPDATE_CAMPAIGN_PERFORMANCE = 'campaigns/UPDATE_CAMPAIGN_PERFORMANCE',
    UPDATE_PERFORMANCE_DISQUALIFY = 'campaigns/UPDATE_PERFORMANCE_DISQUALIFY',
    UPDATE_PERFORMANCE_PAY = 'campaigns/UPDATE_PERFORMANCE_PAY',
    CLEAR_CAMPAIGN_PERFORMANCE = 'campaigns/CLEAR_CAMPAIGN_PERFORMANCE',
    CAMPAIGN_PROGRESS_REQUEST = 'campaigns/CAMPAIGN_PROGRESS_REQUEST',
    SAVE_CAMPAIGN_PROGRESS = 'campaigns/SAVE_CAMPAIGN_PROGRESS',
    CLEAR_CAMPAIGN_PROGRESS = 'campaigns/CLEAR_CAMPAIGN_PROGRESS',
    CLOSE_CAMPAIGN = 'campaigns/CLOSE_CAMPAIGN',
    REMOVE_CAMPAIGN_FROM_SUBMISSIONS_SUMMARIES = 'campaigns/REMOVE_CAMPAIGN_FROM_SUBMISSIONS_SUMMARIES',
    SET_EDITING_CAMPAIGN = 'campaigns/SET_EDITING_CAMPAIGN',
    SET_BLOCKED_CAMPAIGNS = 'campaigns/SET_BLOCKED_CAMPAIGNS',
    EXPORT_PERFORMANCE_REQUEST = 'campaigns/EXPORT_PERFORMANCE_REQUEST',
    EXPORT_PROGRESS_REQUEST = 'campaigns/EXPORT_PROGRESS_REQUEST',
    EXPORT_CAMPAIGN_OVERVIEW = 'campaigns/EXPORT_CAMPAIGN_OVERVIEW',
    BLOCKED_CAMPAIGN_CHECK_REQUEST = 'campaigns/BLOCKED_CAMPAIGN_CHECK_REQUEST',
    SAVE_BLOCKED_CAMPAIGN_CHECK = 'campaigns/SAVE_BLOCKED_CAMPAIGN_CHECK',
    UPDATE_REGISTRATION_MAX_SLOTS = 'campaigns/UPDATE_REGISTRATION_MAX_SLOTS',
    UPDATE_LIVE_REGISTRATION_MAX_SLOTS = 'campaigns/UPDATE_LIVE_REGISTRATION_MAX_SLOTS',
    DUPLICATE_CAMPAIGN_REQUEST = 'campaigns/DUPLICATE_CAMPAIGN_REQUEST',
    DELETE_CAMPAIGN_REQUEST = 'campaigns/DELETE_CAMPAIGN_REQUEST',
    CAMPAIGN_LIST_CANCEL = 'CAMPAIGN_LIST_CANCEL',
    UPDATE_TOTAL_EARNING = 'campaigns/UPDATE_TOTAL_EARNING',
    UPDATE_EARNING_ITEM = 'UPDATE_EARNING_ITEM',
    PAID_OUTSIDE_APP = 'campaigns/PAID_OUTSIDE_APP',
    UPDATE_PAID_OUTSIDE_APP = 'UPDATE_PAID_OUTSIDE_APP',
    SAVE_ADDITIONAL_PAYMENT = 'SAVE_ADDITIONAL_PAYMENT',
    UPDATE_SERVICE_FEE = 'UPDATE_SERVICE_FEE',
    UPDATE_TAX = 'UPDATE_TAX',
    GET_PAYMENT_SUMMARY = 'GET_PAYMENT_SUMMARY',
    CLEAR_PAYMENT_SUMMARY = 'CLEAR_PAYMENT_SUMMARY',
    UPDATE_PAYMENT_SUMMARY = 'UPDATE_PAYMENT_SUMMARY',
    SAVE_PAYMENT_SUMMARY = 'SAVE_PAYMENT_SUMMARY',
    SEND_CAMPAIGN_TO_PAYMENT_REQUEST = 'SEND_CAMPAIGN_TO_PAYMENT_REQUEST',
    SEND_CAMPAIGN_BACK_TO_REVIEW = 'SEND_CAMPAIGN_BACK_TO_REVIEW',
    APPLY_PROMO_CODE = 'APPLY_PROMO_CODE',
    UNAPPLY_PROMO_CODE = 'UNAPPLY_PROMO_CODE',
    TOGGLE_PAY_WITH_BALANCE = 'TOGGLE_PAY_WITH_BALANCE',
    SET_TO_READY_FOR_LAUNCH = 'SET_TO_READY_FOR_LAUNCH',
    SET_GUIDED_TEMPLATE_SETTINGS_MODAL = 'SET_GUIDED_TEMPLATE_SETTINGS_MODAL',
    END_CAMPAIGN = 'campaigns/END_CAMPAIGN',
    CAMPAIGN_SUBMISSIONS_SUMMARY_REQUEST = 'campaigns/CAMPAIGN_SUBMISSIONS_SUMMARY_REQUEST',
    SAVE_CAMPAIGN_SUBMISSIONS_SUMMARY = 'campaigns/SAVE_CAMPAIGN_SUBMISSIONS_SUMMARY',
}

export const removeCampaignFromSubmissionsSummaries = (payload: { uuid: string }) => ({
    payload,
    type: CampaignsActions.REMOVE_CAMPAIGN_FROM_SUBMISSIONS_SUMMARIES,
})

export const closeCampaign = (payload: CloseCampaignRequest) => ({
    payload,
    type: CampaignsActions.CLOSE_CAMPAIGN,
})

export const endCampaign = (payload: { uuid: string }) => ({
    payload,
    type: CampaignsActions.END_CAMPAIGN,
})

export const requestCampaignSubmissionsSummary = (payload: { uuid: string }) => ({
    payload,
    type: CampaignsActions.CAMPAIGN_SUBMISSIONS_SUMMARY_REQUEST,
})

export const saveCampaignSubmissionsSummary = (payload: { uuid: string; data: CampaignSubmissionsSummaryData }) => ({
    payload,
    type: CampaignsActions.SAVE_CAMPAIGN_SUBMISSIONS_SUMMARY,
})

export interface RequestCampaignListPayload {
    loadMore?: boolean
    keywords?: string | null
    country?: string
    team_type?: string[]
    campaign_status?: number[]
    viewType: CampaignViews
}

export const requestCampaignList = (payload: RequestCampaignListPayload) => ({
    payload,
    type: CampaignsActions.CAMPAIGN_LIST_REQUEST,
})

interface SaveCampaignListPayload {
    data: CampaignsResult[] | undefined
    viewType: CampaignViews
}

export const saveCampaignList = (payload: SaveCampaignListPayload) => ({
    payload,
    type: CampaignsActions.SAVE_LIST,
})

interface SaveCampaignPayload {
    data: CampaignsResult | undefined
}

export const saveCampaign = (payload: SaveCampaignPayload) => ({
    payload,
    type: CampaignsActions.SAVE_CAMPAIGN,
})

export const getCampaignSummary = (payload: { uuid: string }) => ({
    payload,
    type: CampaignsActions.CAMPAIGN_SUMMARY_REQUEST,
})

interface SaveCampaignSummaryPayload {
    data: CampaignSummaryState | undefined
}

export const saveCampaignSummary = (payload: SaveCampaignSummaryPayload) => ({
    payload,
    type: CampaignsActions.SAVE_CAMPAIGN_SUMMARY,
})

export interface GetBlockedCampaignCheckPayload {
    baseCampaignUuid: string
    blockedCampaignUuid: string
    stateKey?: STATE_KEYS
    onSuccess?: (campaign: BlockedCampaignCheckResult) => void
    onCampaignNotFound?: () => void
}

export const getBlockedCampaignCheck = (payload: GetBlockedCampaignCheckPayload) => ({
    payload,
    type: CampaignsActions.BLOCKED_CAMPAIGN_CHECK_REQUEST,
})

interface SaveBlockedCampaignPayload {
    data: BlockedCampaignCheckResult | undefined
}

export const saveBlockedCampaignCheck = (payload: SaveBlockedCampaignPayload) => ({
    payload,
    type: CampaignsActions.SAVE_BLOCKED_CAMPAIGN_CHECK,
})

export interface RequestSubmissionsSummaryPayload {
    loadMore?: boolean
    keywords?: string
    viewType: SubmissionsSummaryViews
    country?: string
    team_type?: string[]
    campaign_status?: number[]
}

export const requestSubmissionsSummary = (payload: RequestSubmissionsSummaryPayload) => ({
    payload,
    type: CampaignsActions.SUBMISSIONS_SUMMARY_REQUEST,
})

interface SaveSubmissionsSummaryPayload {
    data: SubmissionsSummaryResult[] | undefined
    viewType: SubmissionsSummaryViews
}

export const saveSubmissionsSummary = (payload: SaveSubmissionsSummaryPayload) => ({
    payload,
    type: CampaignsActions.SAVE_SUBMISSIONS_SUMMARY,
})

export interface RegisterCampaignPayload {
    data: RegisterCampaignRequest
    callback?: (campaignData: RegisterCampaignResponse) => void
}

export const registerCampaign = (payload: RegisterCampaignPayload) => ({
    payload,
    type: CampaignsActions.REGISTER_CAMPAIGN_REQUEST,
})

interface SubmitCampaignPayload {
    data: {
        campaign: CampaignState
        uuid: string
    }
}

export const submitCampaign = (payload: SubmitCampaignPayload) => ({
    payload,
    type: CampaignsActions.SUBMIT_CAMPAIGN_REQUEST,
})

interface UpdateCampaignPayload {
    campaign: EditingCampaignState
    uuid: string
    callback?: () => void
}

export const updateCampaign = (payload: UpdateCampaignPayload) => ({
    payload,
    type: CampaignsActions.UPDATE_CAMPAIGN_REQUEST,
})

interface CampaignActionPayload {
    uuid: string
    onSuccessCallback?: () => void
}

export const duplicateCampaign = (payload: CampaignActionPayload) => ({
    payload,
    type: CampaignsActions.DUPLICATE_CAMPAIGN_REQUEST,
})

export const deleteCampaign = (payload: CampaignActionPayload) => ({
    payload,
    type: CampaignsActions.DELETE_CAMPAIGN_REQUEST,
})

export const deleteCampaignFromList = (payload: CampaignActionPayload) => ({
    payload,
    type: CampaignsActions.DELETE,
})

interface UpdatePayoutDeadlinePayload {
    showToast?: boolean
    payout_deadline_start?: string
    payout_deadline_end?: string
    uuid: string
}

export const updatePayoutDeadline = (payload: UpdatePayoutDeadlinePayload) => ({
    payload,
    type: CampaignsActions.UPDATE_PAYOUT_DEADLINE_REQUEST,
})

export const savePayoutDeadline = (payload: UpdatePayoutDeadlinePayload) => ({
    payload,
    type: CampaignsActions.SAVE_PAYOUT_DEADLINE,
})

export const updateRecommendationSettingRequest = (payload: UpdateProfileRecommendationSettingRequest) => ({
    payload,
    type: CampaignsActions.UPDATE_PROFILE_RECOMMENDATION_SETTING_REQUEST,
})

export const saveProfileRecommendationSetting = (payload: UpdateProfileRecommendationSettingRequest) => ({
    payload,
    type: CampaignsActions.SAVE_PROFILE_RECOMMENDATION_SETTING,
})

export interface UpdateRegistrationMaxSlotsPayload {
    uuid: string
    max_slots: number
}
export const updateRegistrationMaxSlots = (payload: UpdateRegistrationMaxSlotsPayload) => ({
    payload,
    type: CampaignsActions.UPDATE_REGISTRATION_MAX_SLOTS,
})
export const updateLiveRegistrationMaxSLots = (payload: UpdateRegistrationMaxSlotsPayload) => ({
    payload,
    type: CampaignsActions.UPDATE_LIVE_REGISTRATION_MAX_SLOTS,
})
interface PublishCampaignPayload {
    data: {
        campaign: CampaignState
        uuid: string
    }
}

export const publishCampaign = (payload: PublishCampaignPayload) => ({
    payload,
    type: CampaignsActions.PUBLISH_CAMPAIGN_REQUEST,
})

interface SendCampaignToEditPayload {
    data: {
        uuid: string
    }
}

export const sendCampaignToEdit = (payload: SendCampaignToEditPayload) => ({
    payload,
    type: CampaignsActions.SEND_CAMPAIGN_TO_EDIT_REQUEST,
})

interface RequestCampaignPaymentPayload {
    data: {
        uuid: string
    }
}

export const sendCampaignToPaymentRequest = (payload: RequestCampaignPaymentPayload) => ({
    payload,
    type: CampaignsActions.SEND_CAMPAIGN_TO_PAYMENT_REQUEST,
})

interface SendCampaignBackToReviewPayload {
    data: {
        uuid: string
    }
}

export const sendCampaignBackToReview = (payload: SendCampaignBackToReviewPayload) => ({
    payload,
    type: CampaignsActions.SEND_CAMPAIGN_BACK_TO_REVIEW,
})

export interface SetCampaignSubmissionStatusPayload {
    uuid: string
    submissionStatus: number
}

export const setCampaignSubmissionStatus = (payload: SetCampaignSubmissionStatusPayload) => ({
    payload,
    type: CampaignsActions.SET_CAMPAIGN_SUBMISSION_STATUS,
})

export const saveCampaignSubmissionStatus = (payload: SetCampaignSubmissionStatusPayload) => ({
    payload,
    type: CampaignsActions.SAVE_CAMPAIGN_SUBMISSION_STATUS,
})

export interface SetAutoSubmissionStatusPayload {
    isAutoUpdateSubmissionStatusEnabled: boolean
    uuid: string
    full: number | null
    fillingFast: number | null
    isBatchingUpdated: boolean
    batchingEnable: boolean
}

export const setAutoSubmissionStatus = (payload: SetAutoSubmissionStatusPayload) => ({
    payload,
    type: CampaignsActions.SET_AUTO_SUBMISSION_STATUS,
})

export const saveAutoSubmissionStatus = (payload: SetAutoSubmissionStatusPayload) => ({
    payload,
    type: CampaignsActions.SAVE_AUTO_SUBMISSION_STATUS,
})

export interface RequestCampaignProgressPayload {
    loadMore?: boolean
    url?: string
    uuid: string
    offset?: number
    keywords?: string
}

export const getCampaignProgress = (payload: RequestCampaignProgressPayload) => ({
    payload,
    type: CampaignsActions.CAMPAIGN_PROGRESS_REQUEST,
})

export const exportCampaignProgress = (payload: RequestExportCampaignProgressPayload) => ({
    payload,
    type: CampaignsActions.EXPORT_PROGRESS_REQUEST,
})

export const exportOverview = (payload: RequestExportCampaignOverviewPayload) => ({
    payload,
    type: CampaignsActions.EXPORT_CAMPAIGN_OVERVIEW,
})

interface SaveCampaignProgressPayload {
    data: CampaignProgressState[] | undefined
}

export const saveCampaignProgress = (payload: SaveCampaignProgressPayload) => ({
    payload,
    type: CampaignsActions.SAVE_CAMPAIGN_PROGRESS,
})

export const clearCampaignProgress = () => ({
    type: CampaignsActions.CLEAR_CAMPAIGN_PROGRESS,
})

export interface RequestCampaignPerformancePayload {
    loadMore?: boolean
    url?: string
    uuid: string
    offset?: number
    keywords?: string
    tags?: string
}

export const getCampaignPerformance = (payload: RequestCampaignPerformancePayload) => ({
    payload,
    type: CampaignsActions.CAMPAIGN_PERFORMANCE_REQUEST,
})

export const getCampaignPerformanceFiltersRequest = (payload: RequestCampaignPerformancePayload) => ({
    payload,
    type: CampaignsActions.CAMPAIGN_PERFORMANCE_FILTERS_REQUEST,
})

export const exportCampaignPerformance = (payload: RequestExportCampaignPerformancePayload) => ({
    payload,
    type: CampaignsActions.EXPORT_PERFORMANCE_REQUEST,
})

interface SaveCampaignPerformancePayload {
    data: CampaignPerformanceState[] | undefined
}

export const saveCampaignPerformance = (payload: SaveCampaignPerformancePayload) => ({
    payload,
    type: CampaignsActions.SAVE_CAMPAIGN_PERFORMANCE,
})

interface UpdateCampaignPerformancePayload {
    data: any | undefined
    // data: CampaignPerformanceState[] | undefined;
}

export const updateCampaignPerformance = (payload: UpdateCampaignPerformancePayload) => ({
    payload,
    type: CampaignsActions.UPDATE_CAMPAIGN_PERFORMANCE,
})

interface UpdatePerformanceDisqualifyPayload {
    submissionUuid: string
}

export const updatePerformanceDisqualify = (payload: UpdatePerformanceDisqualifyPayload) => ({
    payload,
    type: CampaignsActions.UPDATE_PERFORMANCE_DISQUALIFY,
})

export const updatePerformancePay = (payload: UpdatePerformanceDisqualifyPayload) => ({
    payload,
    type: CampaignsActions.UPDATE_PERFORMANCE_PAY,
})

export const clearCampaignPerformance = () => ({
    type: CampaignsActions.CLEAR_CAMPAIGN_PERFORMANCE,
})

export interface RequestCampaignPerformancePayload {
    loadMore?: boolean
    url?: string
    uuid: string
}

export const setEditingCampaign = (payload: { uuid: string | null }) => ({
    payload,
    type: CampaignsActions.SET_EDITING_CAMPAIGN,
})

export interface GetCampaignPayload {
    uuid: string
    stateKey?: STATE_KEYS
    onSuccess?: (campaign: CampaignsResult) => void
    onCampaignNotFound?: () => void
}

export const getCampaign = (payload: GetCampaignPayload) => ({
    payload,
    type: CampaignsActions.CAMPAIGN_REQUEST,
})

export const setBlockedCampaigns = (payload: { uuids: string[] | null }) => ({
    payload,
    type: CampaignsActions.SET_BLOCKED_CAMPAIGNS,
})

export const updateTotalEarning = (payload: UpdateTotalEarningPayloadWithCallback) => ({
    payload,
    type: CampaignsActions.UPDATE_TOTAL_EARNING,
})

export const saveTotalEarning = (payload: UpdateTotalEarningPayload) => ({
    payload,
    type: CampaignsActions.UPDATE_EARNING_ITEM,
})

export const paidOutsideApp = (payload: PaidOutsideAppPayloadWithCallback) => ({
    payload,
    type: CampaignsActions.PAID_OUTSIDE_APP,
})

export const savePaidOutsideApp = (payload: PaidOutsideAppPayload) => ({
    payload,
    type: CampaignsActions.UPDATE_PAID_OUTSIDE_APP,
})

export const clearCampaignList = () => ({
    type: CampaignsActions.CAMPAIGN_LIST_CLEAR,
})

export const saveAdditionalPayment = (payload: SaveAdditionalPaymentPayload) => ({
    payload,
    type: CampaignsActions.SAVE_ADDITIONAL_PAYMENT,
})

export const updateServiceFee = (payload: UpdateServiceFeePayload) => ({
    payload,
    type: CampaignsActions.UPDATE_SERVICE_FEE,
})

export const updateTax = (payload: UpdateTaxPayload) => ({
    payload,
    type: CampaignsActions.UPDATE_TAX,
})

export const getPaymentSummary = (payload: GetPaymentSummaryPayload) => ({
    payload,
    type: CampaignsActions.GET_PAYMENT_SUMMARY,
})

export const clearPaymentSummary = () => ({
    type: CampaignsActions.CLEAR_PAYMENT_SUMMARY,
})

export const updatePaymentSummary = (payload: UpdatePaymentSummaryPayload) => ({
    payload,
    type: CampaignsActions.UPDATE_PAYMENT_SUMMARY,
})

export const savePaymentSummary = (payload: CampaignPaymentSummaryData) => ({
    payload,
    type: CampaignsActions.SAVE_PAYMENT_SUMMARY,
})

export interface CallbackHandler {
    onError?: () => void
    onSuccess?: () => void
}

export const applyPromoCode = (payload: ApplyPromoCodePayload & CallbackHandler) => ({
    payload,
    type: CampaignsActions.APPLY_PROMO_CODE,
})

export const unapplyPromoCode = (payload: UnapplyPromoCodePayload & CallbackHandler) => ({
    payload,
    type: CampaignsActions.UNAPPLY_PROMO_CODE,
})

export const togglePayWithBalance = (payload: TogglePayWithBalancePayload & CallbackHandler) => ({
    payload,
    type: CampaignsActions.TOGGLE_PAY_WITH_BALANCE,
})

export const setToReadyForLaunch = (payload: SetToReadyForLaunchPayload & CallbackHandler) => ({
    payload,
    type: CampaignsActions.SET_TO_READY_FOR_LAUNCH,
})

export const setGuidedTemplateSettingsModal = (payload: GuidedTemplateSettingsModalState) => ({
    type: CampaignsActions.SET_GUIDED_TEMPLATE_SETTINGS_MODAL,
    payload,
})

export const campaignsReducer = (state: CampaignsState = initialState, action: BaseAction) => {
    switch (action.type) {
        case CampaignsActions.DELETE: {
            const { uuid: deletedCampaignUuid } = action.payload
            return Object.assign({}, state, {
                campaigns: {
                    ...state.campaigns,
                    live: state.campaigns.live.filter(uuid => uuid !== deletedCampaignUuid),
                },
            })
        }
        case CampaignsActions.SAVE_LIST: {
            const { data, viewType } = action.payload
            const uuids = data.map((campaign: CampaignsResult) => campaign.uuid)
            return Object.assign({}, state, {
                campaigns: {
                    ...state.campaigns,
                    [viewType]: _.uniq([...state.campaigns[viewType as CampaignViews], ...uuids]),
                },
                byId: {
                    ...state.byId,
                    campaigns: data.reduce(
                        (memo: CampaignEntitiesState, campaign: CampaignsResult) =>
                            Object.assign({}, memo, { [campaign.uuid]: campaign }),
                        state.byId.campaigns,
                    ),
                },
            })
        }
        case CampaignsActions.SAVE_CAMPAIGN: {
            const { data } = action.payload
            return Object.assign({}, state, {
                byId: {
                    ...state.byId,
                    campaigns: Object.assign({}, state.byId.campaigns, { [data.uuid]: { ...data } }),
                },
            })
        }

        case CampaignsActions.SAVE_BLOCKED_CAMPAIGN_CHECK: {
            const { data } = action.payload
            return Object.assign({}, state, {
                byId: {
                    ...state.byId,
                    checkedBlockedCampaigns: Object.assign({}, state.byId.checkedBlockedCampaigns, {
                        [data.uuid]: { ...data },
                    }),
                },
            })
        }
        case CampaignsActions.SAVE_PAYOUT_DEADLINE: {
            const { uuid, payout_deadline_start, payout_deadline_end } = action.payload
            return Object.assign({}, state, {
                byId: {
                    ...state.byId,
                    campaigns: Object.assign({}, state.byId.campaigns, {
                        [uuid]: {
                            ...state.byId.campaigns[uuid],
                            payout_deadline_start,
                            payout_deadline_end,
                        },
                    }),
                },
            })
        }
        case CampaignsActions.SAVE_PROFILE_RECOMMENDATION_SETTING: {
            const { uuid, profile_recommendation_enabled } = action.payload
            return Object.assign({}, state, {
                byId: {
                    ...state.byId,
                    campaigns: Object.assign({}, state.byId.campaigns, {
                        [uuid]: {
                            ...state.byId.campaigns[uuid],
                            profile_recommendation_enabled,
                        },
                    }),
                },
            })
        }
        case CampaignsActions.SAVE_CAMPAIGN_SUMMARY: {
            const { data } = action.payload
            return Object.assign({}, state, {
                byId: {
                    ...state.byId,
                    campaignSummaries: Object.assign({}, state.byId.campaignSummaries, { [data.uuid]: { ...data } }),
                },
            })
        }
        case CampaignsActions.SAVE_SUBMISSIONS_SUMMARY: {
            const { data, viewType } = action.payload
            const uuids = data.map((campaign: SubmissionsSummaryResult) => campaign.uuid)
            return Object.assign({}, state, {
                submissionsSummaries: {
                    ...state.submissionsSummaries,
                    [viewType]: _.uniq([...state.submissionsSummaries[viewType as SubmissionsSummaryViews], ...uuids]),
                },
                byId: {
                    ...state.byId,
                    submissionsSummaries: data.reduce(
                        (memo: CampaignEntitiesState, campaign: SubmissionsSummaryResult) =>
                            Object.assign({}, memo, {
                                [campaign.uuid]: campaign,
                            }),
                        state.byId.submissionsSummaries,
                    ),
                },
            })
        }
        case CampaignsActions.SAVE_CAMPAIGN_SUBMISSION_STATUS: {
            const { uuid, submissionStatus } = action.payload
            return Object.assign({}, state, {
                byId: {
                    ...state.byId,
                    submissionsSummaries: {
                        ...state.byId.submissionsSummaries,
                        [uuid]: {
                            ...state.byId.submissionsSummaries[uuid],
                            submission_status: submissionStatus,
                        },
                    },
                },
            })
        }
        case CampaignsActions.SAVE_AUTO_SUBMISSION_STATUS: {
            const { uuid, full, fillingFast, batchingEnable } = action.payload
            return Object.assign({}, state, {
                byId: {
                    ...state.byId,
                    submissionsSummaries: {
                        ...state.byId.submissionsSummaries,
                        [uuid]: {
                            ...state.byId.submissionsSummaries[uuid],
                            auto_update_submission_status: true,
                            submission_status_full_min: full,
                            submission_status_filling_fast_min: fillingFast,
                            batching_enable: batchingEnable,
                        },
                    },
                },
            })
        }
        case CampaignsActions.CAMPAIGN_LIST_CLEAR: {
            return Object.assign({}, state, {
                campaigns: initialState.campaigns,
            })
        }
        case CampaignsActions.SUBMISSIONS_SUMMARY_CLEAR: {
            return Object.assign({}, state, {
                submissionsSummaries: initialState.submissionsSummaries,
            })
        }
        case CampaignsActions.SAVE_CAMPAIGN_PROGRESS: {
            const { data } = action.payload
            return Object.assign({}, state, {
                ...state,
                campaignProgress: _.uniqBy([...state.campaignProgress, ...data], 'uuid'),
            })
        }
        case CampaignsActions.CLEAR_CAMPAIGN_PROGRESS: {
            return Object.assign({}, state, {
                ...state,
                campaignProgress: [],
            })
        }
        case CampaignsActions.SAVE_CAMPAIGN_PERFORMANCE: {
            const { data } = action.payload
            return Object.assign({}, state, {
                ...state,
                campaignPerformance: _.uniqBy([...state.campaignPerformance, ...data], 'uuid'),
            })
        }
        case CampaignsActions.UPDATE_CAMPAIGN_PERFORMANCE: {
            const { data } = action.payload
            return Object.assign({}, state, {
                ...state,
                campaignPerformance: _.map(state.campaignPerformance, (performance: CampaignPerformanceState) => {
                    if (data.uuid === performance.uuid) {
                        const { partiposter_profile, ...otherData } = data
                        return { ...otherData, partiposterId: partiposter_profile.uuid }
                    }
                    return performance
                }),
            })
        }
        case CampaignsActions.UPDATE_PERFORMANCE_DISQUALIFY: {
            const { submissionUuid: disqualifyUuid } = action.payload
            return Object.assign({}, state, {
                ...state,
                campaignPerformance: _.map(state.campaignPerformance, (performance: CampaignPerformanceState) => {
                    if (disqualifyUuid === performance.uuid) {
                        performance.status = 2
                    }
                    return performance
                }),
            })
        }
        case CampaignsActions.UPDATE_PERFORMANCE_PAY: {
            const { submissionUuid: payUuid } = action.payload
            return Object.assign({}, state, {
                ...state,
                campaignPerformance: _.map(state.campaignPerformance, (performance: CampaignPerformanceState) => {
                    if (payUuid === performance.uuid) {
                        performance.status = 4
                    }
                    return performance
                }),
            })
        }
        case CampaignsActions.UPDATE_EARNING_ITEM: {
            const { submissionUuid, total_earnings, reason, custom_reason, payout_remark, adjustment_source } =
                action.payload
            return Object.assign({}, state, {
                ...state,
                campaignPerformance: _.map(state.campaignPerformance, (performance: CampaignPerformanceState) => {
                    if (submissionUuid === performance.uuid) {
                        performance.total_earnings = total_earnings
                        performance.earnings_adjustment_reason = reason
                        performance.earnings_adjustment_source = adjustment_source
                        performance.earnings_adjustment_custom_reason = custom_reason
                        performance.payout_remark = payout_remark
                    }
                    return performance
                }),
            })
        }
        case CampaignsActions.UPDATE_PAID_OUTSIDE_APP: {
            const { submissionUuid, payout_remark, reason, custom_reason, payment_method, custom_payment_method } =
                action.payload
            return Object.assign({}, state, {
                ...state,
                campaignPerformance: _.map(state.campaignPerformance, (performance: CampaignPerformanceState) => {
                    if (submissionUuid === performance.uuid) {
                        performance.is_paid_outside_app = true
                        performance.paid_outside_app_reason = reason
                        performance.paid_outside_app_custom_reason = custom_reason
                        performance.paid_outside_app_payment_method = payment_method
                        performance.paid_outside_app_custom_payment_method = custom_payment_method
                        performance.payout_remark = payout_remark
                        performance.status = 4
                    }
                    return performance
                }),
            })
        }
        case CampaignsActions.REMOVE_CAMPAIGN_FROM_SUBMISSIONS_SUMMARIES: {
            return Object.assign({}, state, {
                ...state,
                submissionsSummaries: {
                    active: _.without(state.submissionsSummaries.active, action.payload.uuid),
                    closed: _.without(state.submissionsSummaries.closed, action.payload.uuid),
                },
            })
        }
        case CampaignsActions.CLEAR_CAMPAIGN_PERFORMANCE: {
            return Object.assign({}, state, {
                ...state,
                campaignPerformance: [],
            })
        }
        case CampaignsActions.SET_EDITING_CAMPAIGN: {
            return Object.assign({}, state, {
                ...state,
                editingCampaign: action.payload.uuid,
            })
        }
        case CampaignsActions.SET_BLOCKED_CAMPAIGNS: {
            return Object.assign({}, state, {
                ...state,
                blockedCampaigns: action.payload.uuids,
            })
        }
        case CampaignsActions.UPDATE_LIVE_REGISTRATION_MAX_SLOTS: {
            const { uuid, max_slots } = action.payload
            return Object.assign({}, state, {
                byId: {
                    ...state.byId,
                    campaigns: {
                        ...state.byId.campaigns,
                        [uuid]: {
                            ...state.byId.campaigns[uuid],
                            registration_max_slots: max_slots,
                        },
                    },
                },
            })
        }
        case CampaignsActions.SAVE_PAYMENT_SUMMARY: {
            const data = action.payload
            return Object.assign({}, state, {
                ...state,
                paymentSummary: data,
            })
        }
        case CampaignsActions.CLEAR_PAYMENT_SUMMARY: {
            return Object.assign({}, state, {
                ...state,
                paymentSummary: initialState.paymentSummary,
            })
        }
        case CampaignsActions.SET_GUIDED_TEMPLATE_SETTINGS_MODAL: {
            return Object.assign({}, state, {
                ...state,
                guidedTemplateSettings: action.payload,
            })
        }

        case CampaignsActions.SAVE_CAMPAIGN_SUBMISSIONS_SUMMARY: {
            const { uuid, data } = action.payload
            return Object.assign({}, state, {
                campaignSubmissionsSummary: {
                    ...state.campaignSubmissionsSummary,
                    [uuid]: data,
                },
            })
        }

        default:
            return state
    }
}

export const selectDetails = (state: CampaignsState, uuid: string) => {
    const campaign = state.byId.campaigns[uuid]
    if (campaign) {
        campaign.payout_deadline_start = campaign.payout_deadline_start
            ? fixBackendDateFormat(campaign.payout_deadline_start)
            : campaign.payout_deadline_start
        campaign.payout_deadline_end = campaign.payout_deadline_end
            ? fixBackendDateFormat(campaign.payout_deadline_end)
            : campaign.payout_deadline_end
    }
    return campaign
}

export const selectDetailsById = (state: State): CampaignEntitiesState => selectCampaigns(state).byId.campaigns

export const selectCheckedBlockedCampaignsById = (state: State): BlockCampaignEntitiesState =>
    selectCampaigns(state).byId.checkedBlockedCampaigns

export const selectSummary = (state: CampaignsState, uuid: string) => state.byId.campaignSummaries[uuid]

export const selectSummaryById = (state: State): CampaignSummaryEntitiesState =>
    selectCampaigns(state).byId.campaignSummaries

export const selectProgress = (state: CampaignsState) => state.campaignProgress

export const selectPerformance = (state: CampaignsState) => state.campaignPerformance

export const getList = (state: CampaignsState, viewType: CampaignViews) => {
    return state.campaigns[viewType].map((uuid: string) => {
        const campaignData = state.byId.campaigns[uuid]
        return { ...campaignData }
    })
}

export const getSubmissionsSummary = (state: CampaignsState, viewType: SubmissionsSummaryViews) => {
    return state.submissionsSummaries[viewType].map((uuid: string) => {
        const campaignData = state.byId.submissionsSummaries[uuid]
        if (campaignData) {
            campaignData.payout_deadline_start = campaignData.payout_deadline_start
                ? fixBackendDateFormat(campaignData.payout_deadline_start)
                : campaignData.payout_deadline_start
            campaignData.payout_deadline_end = campaignData.payout_deadline_end
                ? fixBackendDateFormat(campaignData.payout_deadline_end)
                : campaignData.payout_deadline_end
        }
        return {
            ...campaignData,
            status: campaignData.status,
        }
    })
}

export const selectEditingCampaignUuid = (state: State): string | null => selectCampaigns(state).editingCampaign

export const selectEditingCampaign = createSelector(
    selectEditingCampaignUuid,
    selectDetailsById,
    (uuid, campaigns): null | CampaignState => {
        if (!uuid || !campaigns[uuid]) return null
        return campaigns[uuid]
    },
)

export const selectEditingCampaignCountry = (state: State): COUNTRY_ENUM | null => {
    const uuid = selectEditingCampaignUuid(state)
    if (uuid) {
        return selectCampaign(uuid)(state)?.country || null
    }
    return null
}

export const selectBlockedCampaignsUuids = (state: State): string[] | null => selectCampaigns(state).blockedCampaigns

export const selectEditingCampaignBlockedCampaigns = (state: State): BlockedCampaign[] | null => {
    const uuid = selectEditingCampaignUuid(state)
    if (uuid) {
        return selectCampaign(uuid)(state).blocked_campaigns
    }
    return null
}

interface BlockedCampaignDisplayData {
    uuid: string
    title: string
    brand: string
}
export const selectBlockedCampaignsData = createSelector(
    selectBlockedCampaignsUuids,
    selectEditingCampaignBlockedCampaigns,
    selectCheckedBlockedCampaignsById,
    (uuids, currentBlockedCampaigns, blockCampaigns): Record<string, BlockedCampaignDisplayData> => {
        if (!uuids || !currentBlockedCampaigns) return {}

        const data = currentBlockedCampaigns.map(campaign => ({
            uuid: campaign.uuid,
            title: campaign.title,
            brand: campaign.brand.name,
        }))
        const result = _.keyBy(data, 'uuid')
        const existingUuids = data.map(campaign => campaign.uuid)
        uuids.forEach(uuid => {
            if (existingUuids.includes(uuid)) return

            const blockCampaign = blockCampaigns[uuid]
            result[uuid] = {
                uuid: blockCampaign.uuid,
                title: blockCampaign.title,
                brand: blockCampaign.brand.name,
            }
        })

        return result
    },
)

export const selectEditingCampaignType = createSelector(selectEditingCampaign, (campaign): boolean | undefined => {
    return campaign?.self_serve
})

export const selectPaymentSummary = (state: State) => selectCampaigns(state).paymentSummary

export const selectGuidedTemplateSettings = (state: State) => selectCampaigns(state).guidedTemplateSettings
